import React, { useEffect, useRef, useState } from "react";
import { useThread } from "../../../context/ThreadContext";
import { useAuth } from "../../../context/AuthContext";
import AppIcon from "../../../components/ui/AppIcon";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import "./ThreadPanel.css";

function formatTime(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function normalizeReactions(reactions) {
  if (!reactions) return [];
  return reactions.map((r) => ({
    emoji: r.emoji,
    count: r.count || 1,
    users: r.users || [],
  }));
}

function toEmojiChar(emoji) {
  return emoji;
}

export default function ThreadMessage({ reply, isOwn }) {
  const { updateThreadReply, deleteThreadReply } = useThread();
  const { user } = useAuth();

  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const editInputRef = useRef(null);

  useEffect(() => {
    if (isEditing && editInputRef.current) {
      const el = editInputRef.current;
      el.style.height = "auto";
      el.style.height = `${Math.max(el.scrollHeight, 80)}px`;
    }
  }, [isEditing, editContent]);

  if (!reply) return null;

  // Handle deleted state
  if (reply.isDeleted) {
    return (
      <div className={`thread-message ${isOwn ? "own" : ""} deleted`}>
        <div className="thread-message-avatar">
          <img
            src={`https://ui-avatars.com/api/?name=?&background=random`}
            alt="Deleted"
          />
        </div>
        <div className="thread-message-content">
          <div className="thread-message-header">
            <span className="thread-sender-name deleted-user">Unknown</span>
            <span className="thread-timestamp">
              {formatTime(reply.createdAt)}
            </span>
          </div>
          <div className="thread-message-bubble deleted-content">
            <span>Message deleted</span>
          </div>
        </div>
      </div>
    );
  }

  // Handle different data structures - senderId can be an object or string
  const senderObj = reply.senderId || reply.sender;
  const senderName = senderObj?.name || reply.senderName || "Unknown";
  const senderAvatar =
    senderObj?.profilePhoto || reply.senderAvatar || reply.profilePhoto;
  const content = reply.content || "";
  const timestamp = reply.createdAt;
  const reactions = normalizeReactions(reply.reactions || []);

  const handleReactionClick = (emoji) => {
    // Handle reaction toggle - to be implemented with socket
  };

  const handleEditClick = () => {
    setEditContent(content);
    setIsEditing(true);
    setIsHovered(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent("");
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) {
      handleCancelEdit();
      return;
    }
    await updateThreadReply(reply._id, editContent);
    setIsEditing(false);
    setEditContent("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
    setIsHovered(false);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteThreadReply(reply._id);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <>
      <div
        className={`thread-message ${isOwn ? "own" : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="thread-message-avatar">
          <img
            src={
              senderAvatar ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(senderName)}&background=random`
            }
            alt={senderName}
          />
        </div>

        <div className="thread-message-content">
          <div className="thread-message-header">
            <span className="thread-sender-name">
              {isOwn ? "You" : senderName}
            </span>
            <span className="thread-timestamp">
              {formatTime(timestamp)}
              {reply.updatedAt && reply.updatedAt !== reply.createdAt && (
                <span className="edited-label">(edited)</span>
              )}
            </span>
          </div>

          {isEditing ? (
            <div className="thread-edit-container">
              <textarea
                ref={editInputRef}
                className={`thread-edit-input ${reply.type === "code" || /^```|```$/m.test(editContent) ? "thread-edit-input--code" : ""}`}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus

              />
              <div className="thread-edit-actions">
                <span className="thread-edit-hint">
                  escape to cancel • enter to save
                </span>
                <button className="thread-edit-save" onClick={handleSaveEdit}>
                  Save
                </button>
                <button
                  className="thread-edit-cancel"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="thread-message-bubble">{content}</div>
          )}

          {reactions.length > 0 && !isEditing && (
            <div className="thread-message-reactions">
              {reactions.map((reaction, idx) => (
                <button
                  key={idx}
                  className="thread-reaction-badge"
                  onClick={() => handleReactionClick(reaction.emoji)}
                >
                  <span>{toEmojiChar(reaction.emoji)}</span>
                  {reaction.count > 1 && <span>{reaction.count}</span>}
                </button>
              ))}
            </div>
          )}

          {/* Hover action buttons */}
          {isHovered && isOwn && !isEditing && (
            <div className="thread-message-actions">
              <button
                className="thread-action-btn"
                onClick={handleEditClick}
                title="Edit"
              >
                <AppIcon name="PencilSquareIcon" size={14} />
              </button>
              <button
                className="thread-action-btn delete"
                onClick={handleDeleteClick}
                title="Delete"
              >
                <AppIcon name="TrashIcon" size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        isDeleting={isDeleting}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
